import { HistoryItemRow } from '@/features/search-bar/components/history-item-row';
import { SuggestionItem } from '@/features/search-bar/components/suggestion-item';
import type { useSearchHistory } from '@/features/search-bar/hooks/use-search-history';
import type { HistoryItem, Suggestion } from '@/features/search-bar/types';

interface Props {
    listRef: React.RefObject<HTMLUListElement | null>;
    query: string;
    suggestions: Suggestion[];
    loadingSuggestions: boolean;
    history: ReturnType<typeof useSearchHistory>;
    onSelectSuggestion: (suggestion: Suggestion) => void;
    onSelectHistory: (item: HistoryItem) => void;
}

export function SuggestionsDropdown({
    listRef,
    query,
    suggestions,
    loadingSuggestions,
    history,
    onSelectSuggestion,
    onSelectHistory,
}: Props) {
    const {
        searchHistory,
        showAllHistory,
        setShowAllHistory,
        removeFromHistory,
    } = history;

    if (
        !(
            suggestions.length > 0 ||
            loadingSuggestions ||
            (!query.trim() && searchHistory.length > 0)
        )
    ) {
        return null;
    }

    return (
        <ul
            ref={listRef}
            className="no-scrollbar absolute top-full left-0 z-50 mt-1 max-h-72 w-full overflow-auto rounded-lg bg-card text-black shadow-lg sm:w-96 dark:text-foreground"
        >
            {loadingSuggestions ? (
                <li className="px-3 py-2 text-sm text-gray-500">
                    Loading suggestions...
                </li>
            ) : !query.trim() && searchHistory.length > 0 ? (
                <>
                    <li className="px-3 pt-3 pb-1 text-sm font-bold tracking-wide text-gray-900">
                        Your recent searches
                    </li>
                    {(showAllHistory
                        ? searchHistory
                        : searchHistory.slice(0, 3)
                    ).map((item) => (
                        <HistoryItemRow
                            key={item.id}
                            item={item}
                            onClick={() => onSelectHistory(item)}
                            onRemove={(e) => removeFromHistory(item.id, e)}
                        />
                    ))}
                    {searchHistory.length > 3 && (
                        <li
                            className="cursor-pointer px-3 py-2 text-sm font-medium text-blue-600 hover:bg-gray-50"
                            onClick={() => setShowAllHistory((v) => !v)}
                        >
                            {showAllHistory ? 'Show less' : 'Show more'}
                        </li>
                    )}
                </>
            ) : suggestions.length > 0 ? (
                suggestions.map((suggestion) => (
                    <SuggestionItem
                        key={`${suggestion.type}-${suggestion.id}`}
                        s={suggestion}
                        onClick={() => onSelectSuggestion(suggestion)}
                    />
                ))
            ) : (
                <li className="px-3 py-2 text-sm text-gray-500">
                    No suggestions found
                </li>
            )}
        </ul>
    );
}
